import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LineChartOutlined } from '@ant-design/icons';
import moment from 'moment';
import { CashFlowService } from './cashflow.service';
import { CashFlow } from '../../types';
import { useGlobalContext } from '../../store/GlobalContext';
import DetailPage, {
  DetailFields,
  DetailRelated,
  DetailSection,
  DetailStats,
  Field,
  formatBool,
  formatCurrency,
  RelatedItem,
} from '../../components/DetailPage';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const monthName = (month?: number | null) =>
  typeof month === 'number' && month >= 1 && month <= 12 ? MONTHS[month - 1] : null;

const CashflowDetails: React.FC = () => {
  const { cashflowId } = useParams<{ cashflowId: string }>();
  const navigate = useNavigate();
  const { selectedEstate } = useGlobalContext();
  const [cashflow, setCashflow] = useState<CashFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!selectedEstate?.uuid || !cashflowId) {
        setError('No active estate is selected.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setCashflow(await CashFlowService.fetchCashFlowById(selectedEstate.uuid, cashflowId));
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load cashflow');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [cashflowId, selectedEstate?.uuid]);

  /* Quarterly is stored as "every 3 months", so the schedule reads back as an
     interval and a count rather than a named frequency. */
  const schedule = cashflow?.isRecurring
    ? `Every ${cashflow.recurrenceCount || 1} ${cashflow.recurrenceInterval || 'month'}${
        (cashflow.recurrenceCount || 1) === 1 ? '' : 's'
      }`
    : 'One-off';

  const season =
    cashflow?.seasonStartMonth && cashflow?.seasonEndMonth
      ? `${monthName(cashflow.seasonStartMonth)} to ${monthName(cashflow.seasonEndMonth)}`
      : null;

  const isInbound = cashflow?.type === 'Inbound';

  const links: RelatedItem[] = [
    cashflow?.assetId && cashflow?.assetName
      ? {
          key: `asset-${cashflow.assetId}`,
          name: cashflow.assetName,
          meta: 'Asset',
          onClick: () => navigate(`/assets/${cashflow.assetId}`),
        }
      : null,
    cashflow?.liabilityId && cashflow?.liabilityName
      ? {
          key: `liability-${cashflow.liabilityId}`,
          name: cashflow.liabilityName,
          meta: 'Liability',
          onClick: () => navigate(`/liabilities/${cashflow.liabilityId}`),
        }
      : null,
    cashflow?.participantId && cashflow?.participantName
      ? {
          key: `participant-${cashflow.participantId}`,
          name: cashflow.participantName,
          meta: 'Participant',
          onClick: () => navigate(`/participants/${cashflow.participantId}`),
        }
      : null,
  ].filter(Boolean) as RelatedItem[];

  const tags = [
    cashflow?.type ? { label: cashflow.type, color: isInbound ? 'green' : 'volcano' } : null,
    cashflow?.isRecurring ? { label: 'Recurring', color: 'blue' } : null,
  ].filter(Boolean) as { label: string; color?: string }[];

  return (
    <DetailPage
      eyebrow="Cashflow"
      title={cashflow?.name || 'Cashflow'}
      icon={<LineChartOutlined />}
      tags={tags}
      loading={loading}
      error={error}
      backTo="/cashflows"
      backLabel="Back to cashflows"
    >
      <DetailStats
        stats={[
          { label: 'Amount', value: formatCurrency(cashflow?.amount) },
          { label: 'Direction', value: cashflow?.type },
          { label: 'Schedule', value: schedule },
          {
            label: 'Date',
            value: cashflow?.date ? moment(cashflow.date).format('LL') : null,
          },
        ]}
      />

      <DetailSection title="Overview">
        <DetailFields>
          <Field label="Description" value={cashflow?.description} span />
          <Field label="Account number" value={cashflow?.accountNo} />
          <Field
            label="Added"
            value={cashflow?.createdAt ? moment(cashflow.createdAt).format('LL') : null}
          />
        </DetailFields>
      </DetailSection>

      <DetailSection title="Schedule">
        <DetailFields>
          <Field label="Recurring" value={formatBool(cashflow?.isRecurring)} />
          <Field label="Frequency" value={schedule} />
          <Field
            label="Season"
            value={season}
            emptyText={cashflow?.isRecurring ? 'Runs all year' : 'Not applicable'}
          />
        </DetailFields>
      </DetailSection>

      <DetailSection
        title="Linked records"
        empty={!links.length}
        emptyText="This cashflow is not linked to an asset, liability or participant"
      >
        <DetailRelated items={links} />
      </DetailSection>
    </DetailPage>
  );
};

export default CashflowDetails;
